import { useQuery } from "@tanstack/react-query";
import { getStepDetail } from "../api";
import { realtimeQueryFlags, useNawyNowRealtimeLive } from "./realtime-status";

/** Full internal step detail — fetched lazily on first expand of a step card. */
export const useStepDetail = ({
  stepUid,
  enabled,
}: {
  stepUid: string;
  enabled: boolean;
}) => {
  const live = useNawyNowRealtimeLive();
  const query = useQuery({
    enabled: enabled && !!stepUid,
    queryKey: ["nawy-now", "step", stepUid],
    // §Realtime-poll-gate (2026-08-08): the heaviest read in the tab (schema + uploads + extractions +
    // history) ran on library defaults — refetched on every collapse/expand and on every window focus,
    // under a form the agent is typing into. Every write path already invalidates this key, and SSE
    // invalidates it on any change made elsewhere, so re-reading on focus is pure churn.
    //
    // It carries the SAME backstop as the board rather than nothing at all: this is the one query whose
    // staleness an agent can actually see (a document another user just accepted, a field they just
    // reviewed), and an expanded step card can stay mounted for a whole working session — so a silently
    // dead pipeline must not be able to leave it wrong indefinitely.
    staleTime: 30_000,
    ...realtimeQueryFlags(live),
    queryFn: () => getStepDetail(stepUid),
  });

  return {
    ...query,
    detail: query.data,
  };
};
